import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { OptionModel } from '@features/admin/option-management/models/option.model';
import { map, Observable, startWith, Subject, Subscription, takeUntil } from 'rxjs';
import { ResearchPersonnelReportService } from '../service/research-personnel-report-service';
import { AppState } from '@core/app-state/reducers';
import { FormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { PrincipalInvestigatorReport } from '../principal-investigator/models/principal-investigator-report';
import { ResearchPersonnelReport, SearchResearchPersonnel } from './models/research-personnel-report';
import { ResearchPersonnelReportList } from './models/research-personnel-report-list';
import { OptionType } from '@features/admin/option-management/models/option-type.enum';
import { optionSelector } from '@features/admin/option-management/store/option.selectors';
import { Router } from '@angular/router';

@Component({
  selector: 'app-researchpersonnel',
  templateUrl: './researchpersonnel.component.html',
  styleUrls: ['./researchpersonnel.component.scss']
})
export class ResearchpersonnelComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sortHeader!: MatSort;
  private readonly STORAGE_KEY = 'researchPersonnelReport.filters.v1';
  subs$: Subscription[] = [];
  private destroy$ = new Subject<void>();

  institutions!: OptionModel[];
  filteredInstitutions$!: Observable<OptionModel[]>;

  roles!: OptionModel[];
  filteredRoles$!: Observable<OptionModel[]>;

  tableLoading = false;      // for table data fetch
  excelLoading = false;      // for Excel export
  private destroyed$ = new Subject<void>();
  constructor(private store: Store<AppState>, public reportService: ResearchPersonnelReportService, public fb: FormBuilder, private router: Router) { }


  get form() { return this.reportService.form; }

  // Table
  displayedColumns = ['firstName', 'lastName', 'email', 'institution', 'role', 'irbNumber'];
  data: ResearchPersonnelReport[] = [];
  total = 0;



  ngOnInit(): void {
    this.setFormOptions();
    this.restoreState();
    this.fetch();

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.reportService.pageIndex = 1;
        this.reportService.save();

      });
  }
  view(protocolId: number): void {
    this.router.navigate(['/protocol', protocolId]);
  }

  private setFormOptions(): void {
    this.store.select(optionSelector.optionsByType(OptionType.institution))
      .pipe(takeUntil(this.destroy$))
      .subscribe((options: OptionModel[]) => {
        this.institutions = options ?? [];
        this.filteredInstitutions$ = this.form.controls.institutionId.valueChanges.pipe(
          startWith(null),
          map(value => typeof value === 'string' ? value : value?.name),
          map(name => name ? this._filter(name, this.institutions) : this.institutions.slice())
        );
      });

    this.store.select(optionSelector.optionsByType(OptionType.role))
      .pipe(takeUntil(this.destroy$))
      .subscribe((options: OptionModel[]) => {
        this.roles = options ?? [];
        this.filteredRoles$ = this.form.controls.roleId.valueChanges.pipe(
          startWith(null),
          map(value => typeof value === 'string' ? value : value?.name),
          map(name => name ? this._filter(name, this.roles) : this.roles.slice())
        );
      });
  }

  private _filter(name: string, optionList: OptionModel[]): any {
    const filterValue = name.trim().toLowerCase();
    return optionList.filter(option => option.name.trim().toLowerCase().includes(filterValue));
  }

  onSortChange(sort: Sort) {
    this.reportService.handleSortChange(sort, () => this.fetch());
  }

  onPage(evt: PageEvent) {
    this.reportService.pageIndex = evt.pageIndex + 1;
    this.reportService.pageSize = evt.pageSize;

    this.saveState();   // if you persist, read from service in save/restore
    this.fetch();
  }


  reset() {
    this.reportService.clear();
    this.fetch();
  }
  downloadExcel() {
    const dto = this.buildSearchDto(true);
    this.excelLoading = true;
    this.reportService.exportExcel(dto).subscribe({
      next: (blob) => {
        this.excelLoading = false;

        // filename: MM_dd_yyyy
        const date = new Date();
        const pad = (n: number) => n.toString().padStart(2, '0');
        const fileName = `IRB_ResearchPersonnels_${pad(date.getMonth() + 1)}_${pad(date.getDate())}_${date.getFullYear()}.xlsx`;

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => (this.excelLoading = false)
    });
  }

  fetch() {
    const dto = this.buildSearchDto(false);
    this.tableLoading = true;

    this.reportService.getReport(dto).subscribe({
      next: (payload: ResearchPersonnelReportList) => {
        this.tableLoading = false;
        this.data = payload?.report ?? [];
        this.total = payload?.totalCount ?? 0;
      },
      error: () => (this.tableLoading = false)
    });
  }


  displayFn(option: OptionModel | string | null): string {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.name ?? '';
  }



  private buildSearchDto(forExcel: boolean): SearchResearchPersonnel {
    const raw = this.form.getRawValue();

    // institution can be: number | OptionModel | string | null
    const instVal = raw.institutionId as any;
    const institutionId =
      typeof instVal === 'number' ? instVal :
        instVal && typeof instVal.id === 'number' ? instVal.id :
          undefined; // ignore strings / null


 const roleVal = raw.roleId as any;
    const roleId =
      typeof roleVal === 'number' ? roleVal :
        roleVal && typeof roleVal.id === 'number' ? roleVal.id :
          undefined; // ignore strings / null

    return {
      firstName: (raw.firstName || '').trim() || undefined,
      lastName: (raw.lastName || '').trim() || undefined,
      institutionId,                              // ← now number | undefined
      irbNumber: (raw.irbNumber || '').trim() || undefined,
      studyTitle: (raw.studyTitle || '').trim() || undefined,
      roleId,
      startDate: this.toIsoDate(raw.startDate),
      endDate: this.toIsoDate(raw.endDate),
      email: (raw.email || '').trim() || undefined,
      phone: (raw.phone || '').trim() || undefined,
      principalInvestigatorFirstName: (raw.principalInvestigatorFirstName || '').trim() || undefined,
      principalInvestigatorLastName: (raw.principalInvestigatorLastName || '').trim() || undefined,
      principalInvestigatorEmail: (raw.principalInvestigatorEmail || '').trim() || undefined,
      principalInvestigatorPhone: (raw.principalInvestigatorPhone || '').trim() || undefined,
      sort: this.reportService.sort,
      order: this.reportService.order,
      pageIndex: this.reportService.pageIndex,
      pageSize: this.reportService.pageSize,
      forExcel
    };
  }


private toIsoDate(d: any | null | undefined): string | undefined {
  if (!d) return undefined;
  const dt = (d instanceof Date) ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return undefined;
  return dt.toISOString().split('T')[0]; // yyyy-MM-dd
}

  private saveState(): void {
    const raw = this.form.getRawValue();
    const inst = raw.institutionId as any;
    const institutionId =
      typeof inst === 'number' ? inst :
        (inst && typeof inst.id === 'number' ? inst.id : null);

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
      firstName: (raw.firstName || '').trim(),
      lastName: (raw.lastName || '').trim(),
      institutionId,
      pageIndex: this.reportService.pageIndex,
      pageSize: this.reportService.pageSize,
      sort: this.reportService.sort,
      order: this.reportService.order
    }));
  }

  private restoreState(): void {
    const s = localStorage.getItem(this.STORAGE_KEY);
    if (!s) return;
    try {
      const state = JSON.parse(s);
      this.form.patchValue({
        firstName: state.firstName ?? '',
        lastName: state.lastName ?? '',
        institutionId: state.institutionId ?? null
      }, { emitEvent: false });

      this.reportService.pageIndex = state.pageIndex ?? 1;
      this.reportService.pageSize = state.pageSize ?? 10;
      this.reportService.sort = state.sort ?? 'LastName';
      this.reportService.order = state.order ?? 'asc';
    } catch { }
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
